import { getEventPropsBuilder, getPageName, toSnakeCase, toTitleCase } from '../../utils';
import type { AmplitudeEventKeys, AmplitudeGlobalKeys } from '../../amplitude-properties.types';
import { ROUTES } from '@/utils/constants';
import { retrieve, remove, STORAGE_KEYS } from '@/utils/storage';

export type PageViewedEventProps = Partial<AmplitudeEventKeys> & Partial<AmplitudeGlobalKeys>;

type PageViewedEvent = {
    eventName: AmplitudeEventKeys['event'];
    pageName: string;
    pagePath?: string;
    category?: string;
    tag?: string;
    source?: string;
};

const PAGE_VIEW_EVENT = {
    HOME: 'Home Page Viewed',
    CATEGORY: 'Category Page Viewed',
    FDC_JACKPOTS_INFO: 'FDC Jackpots Info Page Viewed'
} as const;

const PAGE_NAME = {
    HOME: 'home',
    CATEGORY: 'category',
    FDC_JACKPOTS_INFO: 'fdc_jackpots_info'
} as const;

const DEFAULT_SOURCE = 'direct';

export const getEpicHubPageViewedProps = (categorySlug: string): PageViewedEventProps => ({
    event: 'Epic Hub Page Viewed',
    'Page Name': 'epic_hub',
    'Game Collection Name': toSnakeCase(categorySlug)
});

const getPageViewedEventName = (pathname: string): AmplitudeEventKeys['event'] => {
    if (pathname === ROUTES.HOMEPAGE) {
        return PAGE_VIEW_EVENT.HOME;
    }

    if (pathname.startsWith('/c/')) {
        return PAGE_VIEW_EVENT.CATEGORY;
    }

    if (pathname === ROUTES.SITE_WIDE_JACKPOT_RULES) {
        return PAGE_VIEW_EVENT.FDC_JACKPOTS_INFO;
    }

    // TODO: @Daniel-Fratila - please refactor this function to call pathname instead of passing through props
    if (pathname === ROUTES.SITE_WIDE_JACKPOT_RULES) {
        return 'FDC Jackpots Info Page Viewed';
    }

    return `${toTitleCase(pathname)} Page Viewed`;
};

export const getPageViewedProps = (e: PageViewedEvent): PageViewedEventProps => {
    const category = e.eventName === PAGE_VIEW_EVENT.HOME ? e.category || '' : toSnakeCase(e.category || '');
    return {
        event: e.eventName,
        'Page Name': toSnakeCase(e.pageName),
        'Page Path': e.pagePath || '',
        Source: e.source || DEFAULT_SOURCE,
        Category: category,
        Tag: e.tag || ''
    };
};

const getCategoryUidFromCategoryPageDom = (): string => {
    const el =
        document.querySelector<HTMLElement>('.category-page[data-t-categoryuid]') ??
        document.querySelector<HTMLElement>('.explore-videos-container[data-t-categoryuid]');
    return el?.getAttribute('data-t-categoryuid') ?? '';
};

const getGamePageRouteAndTrackingProps = (pagePath: string): Partial<PageViewedEventProps> => {
    const [slug] = pagePath.replace(/^\/game\//, '').split('/');
    const isClient = typeof document !== 'undefined';
    return {
        'Game ID': slug ?? '',
        'Full URL': isClient ? `${window.location.origin}${pagePath}` : '',
        'Page Title': isClient ? document.title : '',
        'Game Name': isClient
            ? toSnakeCase(document.querySelector('[data-t-gamename]')?.getAttribute('data-t-gamename') ?? '')
            : '',
        'Game Provider': isClient
            ? toSnakeCase(document.querySelector('[data-t-gameprovider]')?.getAttribute('data-t-gameprovider') ?? '')
            : ''
    };
};

export const buildPageEventProps = getEventPropsBuilder((pagePath: string): PageViewedEventProps => {
    const eventName = getPageViewedEventName(pagePath);

    if (pagePath === ROUTES.HOMEPAGE) {
        return getPageViewedProps({
            eventName,
            pageName: PAGE_NAME.HOME,
            pagePath: ROUTES.HOMEPAGE
        });
    }

    if (pagePath.startsWith(ROUTES.CATEGORY_HUB)) {
        const categorySlug = pagePath.slice(ROUTES.CATEGORY_HUB.length);
        return getEpicHubPageViewedProps(categorySlug);
    }

    if (pagePath.startsWith('/c/')) {
        const categoryUidFromDom = getCategoryUidFromCategoryPageDom();
        const categorySlugFromPath = pagePath.replace('/c/', '').split('/')[0] || '';
        return getPageViewedProps({
            eventName,
            category: categoryUidFromDom || categorySlugFromPath,
            pageName: PAGE_NAME.CATEGORY,
            pagePath
        });
    }

    if (pagePath === ROUTES.SITE_WIDE_JACKPOT_RULES) {
        const storedSource = retrieve(STORAGE_KEYS.NAVIGATION_SOURCE, 'local');
        if (storedSource) {
            remove(STORAGE_KEYS.NAVIGATION_SOURCE, 'local');
        }
        return getPageViewedProps({
            eventName,
            pageName: PAGE_NAME.FDC_JACKPOTS_INFO,
            pagePath,
            source: storedSource || DEFAULT_SOURCE
        });
    }

    if (pagePath.startsWith('/game/')) {
        return {
            ...getPageViewedProps({
                eventName: 'Game Information Page Viewed',
                pageName: 'game_info',
                pagePath,
                source: 'direct'
            }),
            ...getGamePageRouteAndTrackingProps(pagePath)
        };
    }

    // Promo detail pages (/rewards/:promoCode) have their own dedicated
    // "Promo Offer Page Viewed" event fired via onPromoLoaded — skip generic tracking.
    if (/^\/rewards\/[^/]+$/.test(pagePath)) {
        return {};
    }

    const pageName = getPageName();
    return getPageViewedProps({
        eventName,
        pageName,
        pagePath
    });
});
